import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of, throwError } from 'rxjs'
import { catchError, map, retry } from 'rxjs/operators'
import { CustomPaginationType } from '../../features/shared/custom-components/custom-pagination/modals/enum/custom-pagination.enum'
import { DispenserAgreementUpdatePayload } from '../../models/dispensers.model'
import { AgreementType } from '../../models/enum/agreement.enum'
import { SharedErrorType, SharedKey, SharedValue } from '../../models/enum/shared.enum'
import { ISharedPagination } from '../../models/interface/shared.interface'
import { AppConfigurationService } from '../app-configuration.service'
import { SharedService } from '../shared/shared.service'

@Injectable({
  providedIn: 'root'
})
export class AgreementService {
  constructor(
    private http: HttpClient,
    private appConfigurationService: AppConfigurationService,
    private sharedService: SharedService
  ) {}

  public getAgreements(type: AgreementType, params: ISharedPagination): Observable<any> {
    let payload: HttpParams = new HttpParams()
      .set('agreementType', type)
      .set(SharedKey.pageSize, SharedValue.pageSize.toString())

    if (params?.pageNumber) {
      payload = payload.set(SharedKey.currentPage, (params.pageNumber - 1).toString())
    }
    if (params?.sort) {
      payload = payload.set(SharedKey.sort, params.sort)
    }
    if (params?.pageSize) {
      payload = payload.set(SharedKey.pageSize, params.pageSize.toString())
    }
    if (params?.status) {
      payload = payload.set(SharedKey.states, params.status)
    }
    if (params?.searchText) {
      payload = payload.set(SharedKey.searchText, params.searchText)
    }

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/agreements`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        res.pagination.type = CustomPaginationType.CUSTOM
        return res
      }),
      retry(1),
      catchError(() => {
        return of(null)
      })
    )
  }

  public getAgreementDetail(code: string, isDrawDown: boolean = false): Observable<any> {
    let payload: HttpParams = new HttpParams().set(SharedKey.fields, SharedValue.full)

    if (isDrawDown) {
      payload = payload.set('isDrawDown', isDrawDown.toString())
    }

    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/agreement/${code}`
    return this.http.get<any>(url, { params: payload }).pipe(
      retry(1),
      catchError(() => {
        return of(null)
      })
    )
  }

  public getAgreementDetailEntries(
    code: string,
    agreementPageType: string,
    params: ISharedPagination
  ): Observable<any> {
    let payload: HttpParams = new HttpParams()
      .set(SharedKey.fields, SharedValue.full)
      .set(SharedKey.pageSize, SharedValue.pageSize.toString())
      .set('agreementPageType', agreementPageType)

    if (params?.pageNumber) {
      payload = payload.set(SharedKey.currentPage, params.pageNumber.toString())
    }

    const baseSite = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseSite}/users/current/agreement/${code}/entries`
    return this.http.get<any>(url, { params: payload }).pipe(
      map((res) => {
        res.pagination.type = CustomPaginationType.CUSTOM
        return res
      }),
      retry(1),
      catchError((err) => {
        if (err.error?.errors?.length) {
          if (err.error.errors[0].type === 'ModelNotFoundError') {
            return throwError({ type: SharedErrorType.notFound404 })
          }
        }
        return of(null)
      })
    )
  }

  public getDispenserCategories(folType: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/dispensers/category?categoryType=${folType}`
    return this.http.get<any>(url).pipe(retry(1))
  }

  public getDispenserTypes(): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/dispensers/types`
    return this.http.get<any>(url).pipe(
      retry(1),
      catchError(() => {
        return of({})
      })
    )
  }

  public getServiceProducts(serviceType: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/dispensers/services/${serviceType}`
    return this.http.get<any>(url).pipe(
      retry(1),
      catchError(() => {
        return of({})
      })
    )
  }

  public getCategoryProducts(payload: any): Observable<any> {
    const baseSiteUrl: string = this.appConfigurationService.getBaseSiteUrl()
    const postUrl = `${baseSiteUrl}/users/current/dispensers/productsByCategory?fields=DEFAULT`
    return this.http.post(postUrl, payload).pipe(
      retry(1),
      catchError(() => {
        return of({})
      })
    )
  }

  public submitDispenserAgreement(payload: any): Observable<any> {
    const baseSiteUrl: string = this.appConfigurationService.getBaseSiteUrl()
    const postUrl = `${baseSiteUrl}/users/current/dispensers/submitDispenserAgreement`
    return this.http.post(postUrl, payload, { responseType: 'text' }).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public getEndCustomerAddress(agreementCode: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/agreement/${agreementCode}/endUserAddress`
    return this.http.get<any>(url).pipe(
      retry(1),
      catchError(() => {
        return of(null)
      })
    )
  }

  public getDistributorWarehouses(agreementCode: string): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/agreement/${agreementCode}/distributorWarehouses`
    return this.http.get<any>(url).pipe(
      retry(1),
      catchError(() => {
        return of(null)
      })
    )
  }

  public placeDispenserOrder(payload: any, agreementCode: string): Observable<any> {
    const baseSiteUrl: string = this.appConfigurationService.getBaseSiteUrl()
    const postUrl = `${baseSiteUrl}/users/current/agreement/${agreementCode}/createDispenserShipment`
    return this.http.post(postUrl, payload).pipe(retry(1), catchError(this.sharedService.handleError))
  }

  public downloadRequestForm(type: string): Observable<any> {
    const baseSiteUrl: string = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseSiteUrl}/users/current/agreements/${type}/csv`
    return this.http
      .get(url, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(retry(1), catchError(this.sharedService.handleError))
  }

  public uploadRequestForm(type: string, file: any): Observable<any> {
    const baseSiteUrl: string = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseSiteUrl}/users/current/agreements/${type}/import/csv`
    const formData = new FormData()
    formData.set('file', file[0])
    return this.http.post(url, formData).pipe(catchError(this.sharedService.handleError))
  }

  public downloadOpenShipmentOrders(): Observable<any> {
    const baseUrl = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseUrl}/users/current/shipments/shipmentCSV`
    return this.http.get(url).pipe(retry(1), catchError(this.sharedService.handleError))
  }

  public updateDispenserAgreement(
    userdId: string,
    code: string,
    payload: DispenserAgreementUpdatePayload
  ): Observable<any> {
    const baseSiteUrl: string = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseSiteUrl}/users/current/dispensers/uppdateDispenserAgreement/${code}`

    return this.http.patch(url, payload).pipe(
      retry(1),
      catchError(() => {
        throw new Error(SharedValue.errorTryAgain)
      })
    )
  }

  public cancelDispenserAgreement(code: string): Observable<any> {
    const baseSiteUrl: string = this.appConfigurationService.getBaseSiteUrl()
    const url = `${baseSiteUrl}/users/current/dispensers/cancelDispenserAgreement/${code}`
    return this.http.post(url, {}).pipe(catchError(this.sharedService.handleError))
  }
}
